import React, { useState, useRef } from 'react';
import { 
  X, 
  Upload, 
  FileText, 
  AlertCircle, 
  CheckCircle, 
  Clock,
  AlertTriangle,
  Info
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

const DocumentUploadModal = ({ 
  isOpen, 
  onClose, 
  documento, 
  onUploadSuccess 
}) => {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    setError('');
    
    // Validar formato
    const fileExtension = selectedFile.name.split('.').pop().toUpperCase();
    if (!documento.formato_aceito.includes(fileExtension)) {
      setError(`Formato não aceito. Formatos permitidos: ${documento.formato_aceito.join(', ')}`);
      return;
    }
    
    // Validar tamanho
    const fileSizeMB = selectedFile.size / (1024 * 1024);
    if (fileSizeMB > documento.tamanho_maximo_mb) {
      setError(`Arquivo muito grande. Tamanho máximo: ${documento.tamanho_maximo_mb}MB`);
      return;
    }
    
    setFile(selectedFile);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Selecione um arquivo para fazer upload');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setUploadProgress(0);

      // Simular progresso de upload (em produção, usar Firebase Storage com progresso real)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Chamar função de upload no backend
      const uploadDocumento = httpsCallable(functions, 'uploadDocumentoObrigatorio');
      const result = await uploadDocumento({
        fornecedor_id: user?.id_fornecedor || user?.uid,
        documento_obrigatorio_id: documento.id,
        nome_arquivo: file.name,
        tamanho_arquivo: file.size,
        tipo_arquivo: file.type
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.data.success) {
        // Aguardar um pouco para mostrar 100%
        setTimeout(() => {
          onUploadSuccess(result.data.data);
          onClose();
        }, 500);
      } else {
        setError(result.data.message || 'Erro ao fazer upload do documento');
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      setError('Erro ao fazer upload. Tente novamente.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const resetForm = () => {
    setFile(null);
    setError('');
    setUploadProgress(0);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ativo':
        return 'bg-green-100 text-green-800';
      case 'inativo':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              Upload de Documento
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {documento.nome}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Informações do documento */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">{documento.nome}</h4>
              <p className="text-xs text-gray-600 mt-1">{documento.descricao}</p>
            </div>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(documento.status)}`}>
              {documento.status === 'ativo' ? 'Ativo' : 'Inativo'}
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
            <div className="flex items-center">
              <Clock className="h-3 w-3 mr-2" />
              <span>Frequência: {documento.frequencia}</span>
            </div>
            {documento.validade_dias && (
              <div className="flex items-center">
                <Info className="h-3 w-3 mr-2" />
                <span>Validade: {documento.validade_dias} dias</span>
              </div>
            )}
            <div className="flex items-center">
              <FileText className="h-3 w-3 mr-2" />
              <span>Formatos: {documento.formato_aceito.join(', ')}</span>
            </div>
            <div className="flex items-center">
              <AlertTriangle className="h-3 w-3 mr-2" />
              <span>Máx: {documento.tamanho_maximo_mb}MB</span>
            </div>
          </div>
        </div>

        {/* Área de upload */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Selecionar Arquivo
          </label>
          
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragActive 
                ? 'border-blue-400 bg-blue-50' 
                : file 
                  ? 'border-green-400 bg-green-50' 
                  : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {!file ? (
              <div>
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-sm text-gray-600 mb-2">
                  Arraste e solte o arquivo aqui, ou
                </p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  clique para selecionar
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Formatos aceitos: {documento.formato_aceito.join(', ')} | 
                  Tamanho máximo: {documento.tamanho_maximo_mb}MB
                </p>
              </div>
            ) : (
              <div>
                <CheckCircle className="mx-auto h-12 w-12 text-green-400 mb-4" />
                <p className="text-sm font-medium text-gray-900 mb-2">{file.name}</p>
                <p className="text-xs text-gray-600">
                  {formatFileSize(file.size)} • {file.type || 'Tipo não identificado'}
                </p>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="text-red-600 hover:text-red-700 text-sm mt-2"
                >
                  Remover arquivo
                </button>
              </div>
            )}
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileInputChange}
            accept={documento.formato_aceito.map(f => `.${f.toLowerCase()}`).join(',')}
          />
        </div>

        {/* Instruções */}
        {documento.instrucoes_upload && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-400 mr-3 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-blue-900 mb-1">Instruções de Upload</h4>
                <p className="text-sm text-blue-700">{documento.instrucoes_upload}</p>
              </div>
            </div>
          </div>
        )}

        {/* Campos obrigatórios */}
        {documento.campos_obrigatorios.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-400 mr-3 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-yellow-900 mb-1">Campos Obrigatórios</h4>
                <p className="text-sm text-yellow-700">
                  Certifique-se de que o documento contenha: {documento.campos_obrigatorios.join(', ')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progress bar */}
        {uploading && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Fazendo upload...</span>
              <span className="text-sm text-gray-500">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Botões de ação */}
        <div className="flex items-center justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={handleClose}
            disabled={uploading}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={!file || uploading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Fazendo Upload...</span>
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                <span>Fazer Upload</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;

